import { FC, useCallback, useEffect, useMemo } from "react";
import { NavLink } from "react-router";
import { observer } from "mobx-react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";

import CoreCard from "../../../ui/core/CoreCard";
import CoreVirtualizedTable from "../../../ui/core/CoreVirtualizedTable";
import { useObservabilityStore } from "../../../../hooks/useObservabilityStore";
import { formatInstant } from "../../../helpers/dataHelper";
import { formatCurrency } from "./MetricTiles";
import type { UsageActivityFeed } from "../../../../types/observability";
import type {
  ColumnFilterState,
  ColumnFiltersConfig,
  FilterOperator,
  SimpleColumnDef,
} from "../../../../types/table";
import ObservabilityStore from "../../../../stores/ObservabilityStore";
import { CloseIcon } from "../../../icons";
import { CheckIcon } from "../../../icons/CheckIcon";
import { PERMISSIONS } from "../../../../types/auth";
import { useHasPermission } from "../../../../contexts/permissions/useHasPermission";
import UsageDownloadCsv from "./UsageDownloadCsv";
import useNotifier from "../../../../hooks/useNotifier";
import UsageActivityFeedColumnVisibilityButton from "./UsageActivityFeedColumnVisibilityButton";
import { formatAgentTypeLabel } from "../../../helpers/observabilityHelper";

const ROW_HEIGHT = 48;

const renderCell = (value: any) => {
  return (
    <Typography noWrap title={String(value ?? "")} sx={{ textAlign: "left" }}>
      {value || "—"}
    </Typography>
  );
};

const renderNumberCell = (value: number | null | undefined) => {
  return (
    <Typography noWrap sx={{ textAlign: "right", width: "100%" }}>
      {typeof value === "number" && value >= 0 ? formatCurrency(value) : "—"}
    </Typography>
  );
};

const renderBilledCell = (value: number | null | undefined) => {
  return (
    <Typography
      noWrap
      sx={{ fontWeight: 500, textAlign: "right", width: "100%" }}
    >
      {typeof value === "number" ? formatCurrency(value) : "—"}
    </Typography>
  );
};

const renderPaidCell = (value: number | null | undefined) => {
  return (
    <Typography
      noWrap
      sx={{
        fontWeight: 500,
        textAlign: "right",
        width: "100%",
        color:
          typeof value === "number" && value > 0
            ? "success.main"
            : "text.secondary",
      }}
    >
      {typeof value === "number" ? formatCurrency(value) : "—"}
    </Typography>
  );
};

const renderCountCell = (value: number | null | undefined) => {
  return (
    <Typography noWrap sx={{ textAlign: "right", width: "100%" }}>
      {typeof value === "number" ? value : "—"}
    </Typography>
  );
};

const renderBooleanCell = (value: boolean) => {
  if (value === true) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <CheckIcon className="text-green-500" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <CloseIcon className="text-red-500" />
    </Box>
  );
};

// Column definitions for the table, filtered to the visible ones, pinned first.
const useUsageActivityFeedColumns = () => {
  const {
    isUsageActivityFeedColumnVisible,
    usageActivityFeedColumnVisibility,
    isUsageActivityFeedColumnPinned,
  } = useObservabilityStore();
  const hasBillingPermission = useHasPermission(PERMISSIONS.billing);

  const columns: SimpleColumnDef<UsageActivityFeed>[] = useMemo(
    () => [
      {
        id: "started_at",
        header: "Opened",
        accessorKey: "started_at",
        minWidth: 150,
        enableSorting: true,
        cell: (value: string) => renderCell(value ? formatInstant(value) : "—"),
      },
      {
        id: "finished_at",
        header: "Closed",
        accessorKey: "finished_at",
        minWidth: 150,
        enableSorting: true,
        cell: (value: string) => renderCell(value ? formatInstant(value) : "—"),
      },
      {
        id: "classification",
        header: "Type",
        accessorKey: "classification",
        minWidth: 140,
        enableSorting: true,
        cell: (value: string) =>
          renderCell(value ? formatAgentTypeLabel(value) : "—"),
      },
      {
        id: "claim_number",
        header: "Claim ID",
        accessorKey: "claim_number",
        minWidth: 140,
        enableSorting: true,
        cell: (value: string) =>
          value ? (
            <NavLink to={`/claims?claim_number=${value}`}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {value}
              </Typography>
            </NavLink>
          ) : (
            "—"
          ),
      },
      {
        id: "payer",
        header: "Payer",
        accessorKey: "payer",
        minWidth: 120,
        enableSorting: true,
        cell: renderCell,
      },
      {
        id: "user",
        header: "Owner",
        accessorKey: "user_displayName",
        minWidth: 140,
        enableSorting: true,
        cell: renderCell,
      },
      {
        id: "billed_amount",
        header: "Billed",
        accessorKey: "billed_amount",
        minWidth: 120,
        enableSorting: true,
        cell: renderBilledCell,
      },
      {
        id: "paid_amount",
        header: "Paid",
        accessorKey: "paid_amount",
        minWidth: 120,
        enableSorting: true,
        cell: renderPaidCell,
      },
      // Success Fee / Recovered moved to the per-claim table (REV-1824): they
      // are per-claim quantities, and per-case rows double-count them when a
      // claim spans several appeal chats. The feed keeps the per-case Base Fee.
      ...(hasBillingPermission
        ? [
            {
              id: "cost",
              header: "Base Fee",
              accessorKey: "cost",
              minWidth: 120,
              enableSorting: true,
              cell: renderNumberCell,
            },
          ]
        : []),
      {
        id: "has_837",
        header: "837s",
        accessorKey: "has_837",
        minWidth: 120,
        enableSorting: true,
        cell: renderBooleanCell,
      },
      {
        id: "count_835",
        header: "835s",
        accessorKey: "count_835",
        minWidth: 120,
        enableSorting: true,
        cell: renderCountCell,
      },
      {
        id: "count_document_uploads",
        header: "Docs",
        accessorKey: "count_document_uploads",
        minWidth: 150,
        enableSorting: true,
        cell: renderCountCell,
      },
      {
        id: "successful_run",
        header: "Status",
        accessorKey: "successful_run",
        minWidth: 120,
        enableSorting: true,
        cell: renderBooleanCell,
      },
      {
        id: "chat_id",
        header: "Case ID",
        accessorKey: "chat_id",
        minWidth: 230,
        enableSorting: false,
        cell: (value: string) =>
          value ? (
            <NavLink to={`/channels/${value}`}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {value}
              </Typography>
            </NavLink>
          ) : (
            "—"
          ),
      },
    ],
    [hasBillingPermission],
  );

  // Filter columns based on visibility settings, pinned columns first
  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) =>
      isUsageActivityFeedColumnVisible(col.id),
    );
    const pinned = visible.filter((col) =>
      isUsageActivityFeedColumnPinned(col.id),
    );
    const unpinned = visible.filter(
      (col) => !isUsageActivityFeedColumnPinned(col.id),
    );
    return [...pinned, ...unpinned];
  }, [
    columns,
    isUsageActivityFeedColumnVisible,
    usageActivityFeedColumnVisibility,
    isUsageActivityFeedColumnPinned,
  ]);

  return { visibleColumns };
};

// Actions (CSV download, column visibility) for the activity feed table,
// rendered separately from the table itself so the dashboard can place them
// beside the tabs instead of inside the card body.
export const UsageActivityFeedCardActions: FC = observer(() => {
  const {
    usageActivityFeed,
    isUsageActivityFeedLoading,
    isUsageActivityFeedLoadingMore,
    downloadUsageActivityFeedCsv,
  } = useObservabilityStore();
  const notifier = useNotifier();
  const { visibleColumns } = useUsageActivityFeedColumns();

  const handleDownload = useCallback(async () => {
    const result = await downloadUsageActivityFeedCsv(
      visibleColumns.map((col) => col.id),
    );

    if (!result.success && result.error) notifier.showError(result.error);
  }, [downloadUsageActivityFeedCsv, notifier, visibleColumns]);

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mr: 3 }}>
      <UsageDownloadCsv
        onDownload={handleDownload}
        disabled={
          isUsageActivityFeedLoading ||
          isUsageActivityFeedLoadingMore ||
          !usageActivityFeed?.length
        }
      />
      <UsageActivityFeedColumnVisibilityButton />
    </Box>
  );
});

const UsageActivityFeedCard: FC = observer(() => {
  const {
    isUsageActivityFeedLoading,
    isUsageActivityFeedLoadingMore,
    usageActivityFeed,
    usageActivityFeedTotal,
    usageActivityFeedTotals,
    usageActivityFeedHasMore,
    usageActivityFeedSort,
    setUsageActivityFeedSort,
    loadUsageActivityFeed,
    loadMoreUsageActivityFeed,
    usageActivityFeedColumnFilters,
    isUsageActivityFeedColumnPinned,
    toggleUsageActivityFeedColumnPin,
    setUsageActivityFeedColumnFilter,
    clearUsageActivityFeedColumnFilter,
    getUniqueUsageActivityFeedColumnValues,
    getUsageActivityFeedColumnFilterValues,
    getUsageActivityFeedColumnFilterOperator,
    usageActivityFeedOptions,
    getUsageActivityFeedOptionLabel,
  } = useObservabilityStore();
  const hasBillingPermission = useHasPermission(PERMISSIONS.billing);

  // Backend options span all feed data; fall back to the loaded rows for
  // columns the options endpoint doesn't cover. Backend options are keyed on
  // their values (labels come from getUsageActivityFeedOptionLabel), so
  // options sharing a display name stay separately selectable.
  const getColumnFilterOptions = useCallback(
    (columnId: string): string[] => {
      const options = usageActivityFeedOptions[columnId];

      if (options?.length) {
        return options.map((option) => String(option.value));
      }

      return getUniqueUsageActivityFeedColumnValues(columnId);
    },
    [usageActivityFeedOptions, getUniqueUsageActivityFeedColumnValues],
  );

  useEffect(() => {
    loadUsageActivityFeed(hasBillingPermission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBillingPermission]);

  const filterableColumns = useMemo(() => {
    const columns = ObservabilityStore.usageActivityFeedFilterableColumns;
    if (hasBillingPermission) return columns;
    return columns.filter((id) => id !== "cost");
  }, [hasBillingPermission]);

  const columnFilterTypes = useMemo(
    () => ObservabilityStore.usageActivityFeedColumnFilterTypes,
    [],
  );

  const columnFilterMaxDate = useMemo(
    () => ({
      started_at: dayjs().format("YYYY-MM-DD"),
      finished_at: dayjs().format("YYYY-MM-DD"),
    }),
    [],
  );

  const columnFiltersFromStore = useMemo(() => {
    const filters: Record<string, ColumnFilterState> = {};
    filterableColumns.forEach((columnId) => {
      filters[columnId] = {
        operator: getUsageActivityFeedColumnFilterOperator(columnId),
        values: getUsageActivityFeedColumnFilterValues(columnId),
      };
    });
    return filters;
  }, [
    filterableColumns,
    usageActivityFeedColumnFilters,
    getUsageActivityFeedColumnFilterOperator,
    getUsageActivityFeedColumnFilterValues,
  ]);

  const columnFiltersConfig: ColumnFiltersConfig = useMemo(
    () => ({
      filters: columnFiltersFromStore,
      getOptions: getColumnFilterOptions,
      getOptionLabel: getUsageActivityFeedOptionLabel,
      onApply: (
        columnId: string,
        values: string[],
        operator?: FilterOperator,
      ) => setUsageActivityFeedColumnFilter(columnId, values, operator),
      onClear: clearUsageActivityFeedColumnFilter,
      filterableColumns: [...filterableColumns],
      columnFilterTypes,
      columnFilterMaxDate,
      isColumnPinned: isUsageActivityFeedColumnPinned,
      onTogglePin: toggleUsageActivityFeedColumnPin,
      canPinColumn: (columnId: string) => columnId !== "started_at",
    }),
    [
      columnFiltersFromStore,
      getColumnFilterOptions,
      getUsageActivityFeedOptionLabel,
      setUsageActivityFeedColumnFilter,
      clearUsageActivityFeedColumnFilter,
      filterableColumns,
      columnFilterTypes,
      columnFilterMaxDate,
      isUsageActivityFeedColumnPinned,
      toggleUsageActivityFeedColumnPin,
    ],
  );

  const { visibleColumns } = useUsageActivityFeedColumns();

  return (
    <CoreCard sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CoreVirtualizedTable<UsageActivityFeed>
          isResizable
          pinColumns
          isLoading={isUsageActivityFeedLoading}
          disableFirstOfTypeStyles
          data={usageActivityFeed ?? []}
          columns={visibleColumns}
          height="100%"
          rowHeight={ROW_HEIGHT}
          minWidth={480}
          enableSorting
          sorting={usageActivityFeedSort}
          onSortingChange={setUsageActivityFeedSort}
          getRowId={(row) => row.id}
          emptyMessage="No activity data available"
          columnFilters={columnFiltersConfig}
          onLoadMore={loadMoreUsageActivityFeed}
          hasMore={usageActivityFeedHasMore}
          isLoadingMore={isUsageActivityFeedLoadingMore}
        />
      </Box>

      <FeedFooter
        loadedCount={usageActivityFeed?.length ?? 0}
        total={usageActivityFeedTotal}
        totals={hasBillingPermission ? usageActivityFeedTotals : null}
      />
    </CoreCard>
  );
});

// Grand-total footer: a "showing N of M" counter plus, for billing members, the
// base-fee total across the WHOLE filtered set (served by the backend, not
// summed from loaded rows — so it stays correct as you scroll). Success fee
// totals live in the per-claim table (REV-1824), not here.
const FeedFooter: FC<{
  loadedCount: number;
  total: number;
  totals: {
    cost: number;
    contingency_cost: number;
    recovered_amount: number;
  } | null;
}> = ({ loadedCount, total, totals }) => (
  <Box
    sx={{
      mt: 1,
      pt: 1,
      borderTop: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
    }}
  >
    <Typography variant="caption" color="text.secondary">
      Showing {loadedCount.toLocaleString()} of {total.toLocaleString()} cases
    </Typography>

    {totals ? (
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <FooterTotal label="Total base fee" value={totals.cost} />
      </Box>
    ) : null}
  </Box>
);

const FooterTotal: FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <Typography variant="caption" color="text.secondary">
    {label}: <b style={{ color: "inherit" }}>{formatCurrency(value)}</b>
  </Typography>
);

export default UsageActivityFeedCard;
